import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import * as React from "react";

function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return <RadioGroupPrimitive.Root className={cn("flex gap-4", className)} {...props} ref={ref} />;
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        "aspect-square size-5 rounded-full border-2 border-gray-300 shadow-sm outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00AE9B] disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-[#00AE9B] data-[state=checked]:bg-[#00AE9B] hover:border-[#00AE9B] hover:bg-[#E8F3F2] transition-all duration-200",
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center text-white">
        <svg
          width="8"
          height="8"
          viewBox="0 0 8 8"
          fill="currentcolor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="4" cy="4" r="4" />
        </svg>
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

interface RadioButtonProps {
  question?: string;
  name?: string;
  defaultValue?: string;
  required?: boolean;
  disabled?: boolean;
  onValueChange?: (value: string) => void;
  className?: string;
  options?: Array<{ value: string; label: string }>;
}

const RadioButton = ({
  question = "Do you have any allergies?",
  name = "allergies",
  defaultValue,
  required = false,
  disabled = false,
  onValueChange,
  className,
  options = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" }
  ]
}: RadioButtonProps) => {
  const id = React.useId();
  
  return (
    <fieldset className={cn("space-y-4", className)}>
      <legend className="text-base font-semibold text-[#212424] mb-3">
        {question}
        {required && <span className="text-red-500 ml-1">*</span>}
      </legend>
      <RadioGroup 
        name={name}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        disabled={disabled}
        className="flex gap-4"
      >
        {options.map((option) => (
          <label 
            key={option.value}
            htmlFor={`${id}-${option.value}`}
            className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-[#00AE9B] hover:bg-[#E8F3F2] transition-all duration-200 cursor-pointer group flex-1"
          >
            <RadioGroupItem
              id={`${id}-${option.value}`}
              value={option.value}
              className="peer"
            />
            <span className="text-[#212424] font-medium group-hover:text-[#00AE9B] transition-colors duration-200">
              {option.label}
            </span>
          </label>
        ))}
      </RadioGroup>
    </fieldset>
  );
};

export { RadioGroup, RadioGroupItem, RadioButton };
export default RadioButton; 